import { EditorContent, useEditor } from '@tiptap/react';
import { cn } from '@src/design-system/helpers';
import { TiptapToolbar } from './tiptap-toolbar';
import { BaseExtensions } from '../extensions/base-extensions';
import { MentionExtension } from '../extensions/mention-extension';

import { useEffect, useState, type ComponentProps } from 'react';
import { registerVariableInsertEditor } from '@src/shared/features/reminders/helpers/append-html';

interface TiptapProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  editorProps?:
    | ComponentProps<typeof EditorContent>
    | React.HTMLAttributes<HTMLElement>;
  enableMention?: boolean;
  contentClassName?: string;
}

const Tiptap: React.FC<TiptapProps> = ({
  content = '',
  onChange,
  placeholder = 'Start typing...',
  editable = true,
  enableMention = false,
  editorProps,
  contentClassName,
}) => {
  const [isEmpty, setIsEmpty] = useState(() => !content?.trim());

  const editor = useEditor({
    extensions: [
      ...BaseExtensions,
      ...(enableMention ? [MentionExtension] : []),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      const empty = editor.isEmpty;
      setIsEmpty(empty);
      onChange?.(empty ? '' : editor.getHTML());
    },
    onCreate: ({ editor }) => {
      setIsEmpty(editor.isEmpty);
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
          'min-h-[200px] p-x-4 border-0'
        ),
      },
    },
  });

  useEffect(() => {
    registerVariableInsertEditor(editor);
    return () => registerVariableInsertEditor(null);
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.isEmpty ? '' : editor.getHTML();
    const newContent = content || '';

    if (currentContent !== newContent) {
      editor.commands.setContent(newContent, { emitUpdate: false });
      setIsEmpty(editor.isEmpty);
    }
  }, [content, editor]);

  return (
    <div
      className={cn(
        `relative w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-pale-border`,
        '[&_.ProseMirror_ul]:!pl-4 [&_.ProseMirror_ul]:!ml-4 [&_.ProseMirror_ul>li]:!list-disc',
        '[&_.ProseMirror_ol]:!pl-4 [&_.ProseMirror_ol>li]:!list-decimal',
        '[&_.ProseMirror_blockquote]:!border-l-4 [&_.ProseMirror_blockquote]:!border-gray-300 [&_.ProseMirror_blockquote]:!pl-4 [&_.ProseMirror_blockquote]:!text-gray-500',
        `[&_.ProseMirror_h1]:text-4xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-4 [&_.ProseMirror_h1]:mb-2 
        [&_.ProseMirror_h1]:leading-tight [&_.ProseMirror_h2]:text-3xl [&_.ProseMirror_h2]:font-semibold
        [&_.ProseMirror_h2]:mt-3 [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:leading-snug
        [&_.ProseMirror_h3]:text-2xl [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-3 [&_.ProseMirror_h3]:mb-2
        [&_.ProseMirror_h3]:leading-snug [&_.ProseMirror_h4]:text-xl [&_.ProseMirror_h4]:font-medium [&_.ProseMirror_h4]:mt-2
        [&_.ProseMirror_h4]:mb-1 [&_.ProseMirror_h5]:text-lg [&_.ProseMirror_h5]:font-medium [&_.ProseMirror_h5]:mt-2
        [&_.ProseMirror_h5]:mb-1 [&_.ProseMirror_h6]:text-base [&_.ProseMirror_h6]:font-medium [&_.ProseMirror_h6]:mt-2
        [&_.ProseMirror_h6]:mb-1 [&_.ProseMirror_h6]:uppercase [&_.ProseMirror_h6]:tracking-wide`,
        '[&_.ProseMirror_code]:bg-black [&_.ProseMirror_code]:text-white [&_.ProseMirror_code]:px-1',
        '[&_.ProseMirror_a]:text-blue-600 hover:[&_.ProseMirror_a]:hover:underline! [&_.ProseMirror_a]:underline-offset-2 [&_.ProseMirror_a]:decoration-2 [&_.ProseMirror_a]:cursor-pointer',
        '[&_a.mention]:font-medium [&_a.mention]:bg-primary-50 [&_a.mention]:text-primary-800 [&_a.mention]:rounded-md [&_a.mention]:px-1 [&_a.mention]:py-px',
        contentClassName
      )}
      role='editor'
    >
      {editable && <TiptapToolbar editor={editor} />}

      <div className='relative prose prose-sm mx-auto max-w-full min-w-0 p-2 break-words [&_.ProseMirror]:max-w-full'>
        {isEmpty && editable ? (
          <span
            aria-hidden='true'
            className='pointer-events-none absolute left-6 top-2 z-0 text-sm text-gray-400'
          >
            {placeholder}
          </span>
        ) : null}
        <EditorContent
          disabled={!editable}
          editor={editor}
          className='ProseMirror relative z-[1]'
          {...editorProps}
        />
      </div>
    </div>
  );
};
export { Tiptap, type TiptapProps };
